/**
 * Auth form skeleton loaders.
 *
 * Rendered inside PublicLayout via AuthPublicLayoutClient - must NOT wrap PublicLayout again.
 * Hero/header copy is provided by PublicLayout; these skeletons cover only the form area.
 */

"use client";

import { Box, Flex, SimpleGrid, Skeleton, VStack } from "@chakra-ui/react";
import { usePathname } from "next/navigation";

import { PageTypePortal } from "@/lib/types";

function FormFieldSkeleton({
  labelWidth,
  controlHeight = "44px",
}: {
  labelWidth: string;
  controlHeight?: string;
}) {
  return (
    <Box>
      <Skeleton height="14px" width={labelWidth} mb={2} borderRadius="4px" />
      <Skeleton height={controlHeight} width="100%" borderRadius="8px" />
    </Box>
  );
}

function SignInFormSkeleton() {
  return (
    <Flex direction="column" w="100%" gap={6}>
      <VStack spacing={4} align="stretch">
        <FormFieldSkeleton labelWidth="48px" />
        <FormFieldSkeleton labelWidth="72px" />
        <Skeleton height="44px" width="100%" borderRadius="8px" mt={2} />
      </VStack>
      <Skeleton height="16px" width="200px" borderRadius="4px" mx="auto" />
    </Flex>
  );
}

function SignUpFormSkeleton() {
  return (
    <Flex
      w="100%"
      mx="auto"
      justify="center"
      direction="column"
      maxW={{ base: "100%", md: "1100px" }}
      gap={5}
    >
      <Flex gap="15px" flexDirection={{ base: "column", md: "row" }}>
        <Skeleton height="44px" width={{ base: "100%", md: "150px" }} borderRadius="8px" />
        <Skeleton height="44px" width={{ base: "100%", md: "150px" }} borderRadius="8px" />
      </Flex>
      <Skeleton height="1px" width="100%" borderRadius="1px" />
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing="24px">
        <FormFieldSkeleton labelWidth="48px" />
        <FormFieldSkeleton labelWidth="112px" />
        <FormFieldSkeleton labelWidth="72px" />
        <FormFieldSkeleton labelWidth="140px" />
        <FormFieldSkeleton labelWidth="80px" />
        <FormFieldSkeleton labelWidth="72px" />
        <FormFieldSkeleton labelWidth="140px" />
        <FormFieldSkeleton labelWidth="48px" />
      </SimpleGrid>
      <Skeleton height="44px" width={{ base: "100%", md: "200px" }} borderRadius="8px" />
    </Flex>
  );
}

function SmallAuthFormSkeleton() {
  return (
    <Flex direction="column" w="100%" gap={6}>
      <VStack spacing={3} align="stretch">
        <Skeleton height="32px" width="70%" borderRadius="6px" />
        <Skeleton height="16px" width="100%" borderRadius="4px" />
        <Skeleton height="16px" width="85%" borderRadius="4px" />
      </VStack>
      <VStack spacing={4} align="stretch">
        <FormFieldSkeleton labelWidth="48px" />
        <FormFieldSkeleton labelWidth="72px" />
        <Skeleton height="44px" width="100%" borderRadius="8px" mt={2} />
      </VStack>
      <Skeleton height="16px" width="120px" borderRadius="4px" />
    </Flex>
  );
}

export function AuthFormSkeleton() {
  const pathname = usePathname();

  if (pathname === PageTypePortal.SignUp) {
    return <SignUpFormSkeleton />;
  }

  if (
    pathname === PageTypePortal.ForgotPassword ||
    pathname === PageTypePortal.ResetPassword
  ) {
    return <SmallAuthFormSkeleton />;
  }

  return <SignInFormSkeleton />;
}
